import { Pencil } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useCategoryPagesList, useDeleteCategoryPage } from '../use-category-pages';

import type { DataGridColumn, DataGridFilter } from '@/components/data-grid/types';
import type { ListScreenColumns } from '@/components/list-screen/types';
import type {
  WebsiteCategoryPageListItem,
  WebsiteCategoryPageListQuery,
  WebsitePublishStatus,
} from '@utazzo/shared';

import { ListScreen } from '@/components/list-screen/ListScreen';
import { ViewOnSiteLink } from '@/components/view-on-site-link';
import { useCurrentUser } from '@/features/auth/use-auth';
import { WEBSITE_PUBLISH_STATUS_LABEL, humanEnum } from '@/lib/enum-labels';
import { categoryPublicUrl } from '@/lib/website-urls';

const STATUS_TONE: Record<WebsitePublishStatus, string> = {
  DRAFT: 'bg-zinc-100 text-zinc-600',
  PENDING_REVIEW: 'bg-amber-50 text-amber-700',
  PUBLISHED: 'bg-emerald-50 text-emerald-700',
  ARCHIVED: 'bg-rose-50 text-rose-700',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_REVIEW', label: 'In review' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

/** See DestinationPagesListPage - deep-link target for the dashboard chips. */
const COMPLETENESS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'INCOMPLETE', label: 'Missing info' },
];

const FILTERS: ReadonlyArray<DataGridFilter> = [
  { id: 'status', label: 'Status', options: STATUS_OPTIONS, widthClass: 'w-48' },
  {
    id: 'completeness',
    label: 'Completeness',
    options: COMPLETENESS_OPTIONS,
    widthClass: 'w-44',
  },
];

export const CategoryPagesListPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deletePage = useDeleteCategoryPage();

  const columns: ListScreenColumns<WebsiteCategoryPageListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<WebsiteCategoryPageListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'title',
        header: 'Category page',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.title}</span>
            <ViewOnSiteLink href={categoryPublicUrl(row.slug)} label={`/${row.slug}`} />
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <span
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}
          >
            {humanEnum(WEBSITE_PUBLISH_STATUS_LABEL, row.status)}
          </span>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<WebsiteCategoryPageListItem, WebsiteCategoryPageListQuery>
      title="Category pages"
      description="Roll-up marketing pages (e.g. India tour packages) - hero, theme tabs, destination grid, best sellers, FAQ, reviews."
      useList={useCategoryPagesList}
      defaultSort={{ by: 'updatedAt', dir: 'desc' }}
      minSearchLength={3}
      toQuery={(state, base) => ({
        ...base,
        ...(state.filters.status ? { status: state.filters.status as WebsitePublishStatus } : {}),
        ...(state.filters.completeness === 'INCOMPLETE' ? { incomplete: true } : {}),
      })}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/category-pages/${row.id}`)}
      createButton={{
        label: 'Add category page',
        onClick: () => navigate('/category-pages/new'),
      }}
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            icon: <Pencil className="h-4 w-4" />,
            onSelect: () => navigate(`/category-pages/${row.id}`),
          },
        ],
        delete: {
          onDelete: (row) => deletePage.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this category page?',
            description: `${row.title} will be soft-deleted.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by title or slug (min 3 characters)',
        emptyTitle: 'No category pages yet',
        emptyDescription: 'Create one to build a public category landing page.',
        errorTitle: 'Failed to load category pages',
        errorDescription: 'Please retry.',
      }}
    />
  );
};
